import React from 'react';
import { Box, Grid } from '@mui/material';
import { useChartCapture } from '../../../hooks';
import SentimentCard from '../../SentimentCard';
import ChartCard from '../../ChartCard';
import ForecastConfidenceCard from '../../ForecastConfidenceCard';
import CardTitle from '../../common/CardTitle';

export interface ResultsMetricsProps {
  forecast: any;
  uiScore: number;
  reputationInfo: {
    level: string;
    color: string;
  };
}

const ResultsMetrics: React.FC<ResultsMetricsProps> = ({
  forecast,
  uiScore,
  reputationInfo
}) => {
  const { handleChartRendered } = useChartCapture();

  return (
    <Box sx={{ px: 0 }}>
      <Grid container spacing={3}>
        {/* Reputation Impact Card (replacing Audience Sentiment) */}
        <Grid item xs={12} md={4}>
          <SentimentCard 
            score={uiScore}
            sentimentLevel={reputationInfo}
            displayMode="reputation"
            isPdfMode={false}
            onGaugeRendered={handleChartRendered}
            chartName="gaugeChart"
            titleComponent={
              <CardTitle 
                title="Reputation Impact" 
                tooltip="Overall change in brand sentiment predicted for this post, weighted by forecast certainty and how strongly the content aligns or clashes with fan expectations."
              />
            }
          />
        </Grid>
        
        {/* Sentiment Analysis Card */}
        <Grid item xs={12} md={4}>
          <ChartCard 
            positivePct={forecast?.positivePct || 0}
            neutralPct={forecast?.neutralPct || 0}
            negativePct={forecast?.negativePct || 0}
            posCount={forecast?.replies7d ? Math.round(forecast.replies7d * (forecast.positivePct || 0) / 100) : undefined}
            neuCount={forecast?.replies7d ? Math.round(forecast.replies7d * (forecast.neutralPct || 0) / 100) : undefined}
            negCount={forecast?.replies7d ? forecast.replies7d - (Math.round(forecast.replies7d * (forecast.positivePct || 0) / 100) + Math.round(forecast.replies7d * (forecast.negativePct || 0) / 100)) : undefined}
            isPdfMode={false}
            onChartRendered={handleChartRendered}
            chartName="sentimentChart"
            titleComponent={
              <CardTitle 
                title="Sentiment Analysis" 
                tooltip="Breakdown of projected audience reactions to this post, based on sentiment analysis. Engagement figures are forecasts based on historical averages. Actual results may vary with algorithm changes or ad spend."
              />
            }
          />
        </Grid>
        
        {/* Confidence Card */}
        <Grid item xs={12} md={4}>
          <ForecastConfidenceCard 
            confidence={forecast?.confidence || 'Low'}
            titleComponent={
              <CardTitle 
                title="Forecast Confidence" 
                tooltip="How confident the model is in its predictions, based on historical data quality and pattern recognition."
              />
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ResultsMetrics;